import { useMemo } from 'react';

/**
 * handle use interface ProductInterface
 */
// eslint-disable-next-line
type ProductInterface = any;

export const hasSpecialPrice = (product: ProductInterface) => {
    const { special_price: specialPrice, special_from_date: specialFromDate, special_to_date: specialToDate } = product;
    if (specialPrice && specialFromDate && specialToDate) {
        const now = Date.now();
        const from = new Date(specialFromDate).getTime();
        const to = new Date(specialToDate).getTime();
        return now >= from && now <= to;
    }
    return false;
};

export const getSpecialPrice = (product: ProductInterface): number => {
    const { special_price: specialPrice } = product;
    if (hasSpecialPrice(product)) {
        return specialPrice ?? 0;
    }
    return 0;
};

export const getFinalPrice = (product: ProductInterface) => {
    const { price_range: priceRange } = product;
    const {
        minimum_price: { final_price: minPrice },
        maximum_price: maximumPrice,
    } = priceRange;
    const maxPrice = maximumPrice ? maximumPrice.final_price : minPrice;
    return maxPrice.value! > minPrice.value! ? maxPrice : minPrice;
};

export const getBasePrice = (product: ProductInterface) => {
    const { price_range: priceRange } = product;
    const {
        minimum_price: { regular_price: minPrice },
        maximum_price: maximumPrice,
    } = priceRange;
    const maxPrice = maximumPrice ? maximumPrice.regular_price : minPrice;
    return maxPrice.value! > minPrice.value! ? maxPrice : minPrice;
};

export const getMinimalPrice = (product: ProductInterface) => {
    const { price_range: priceRange } = product;
    const {
        minimum_price: { final_price: minPrice },
        maximum_price: maximumPrice,
    } = priceRange;
    const maxPrice = maximumPrice ? maximumPrice.final_price : minPrice;
    return maxPrice.value! > minPrice.value! ? minPrice : maxPrice;
};

export const getMaximumPrice = (product: ProductInterface) => {
    const { price_range: priceRange } = product;
    const {
        minimum_price: { final_price: minPrice },
        maximum_price: maximumPrice,
    } = priceRange;
    const maxPrice = maximumPrice ? maximumPrice.final_price : minPrice;
    return maxPrice.value! > minPrice.value! ? maxPrice : minPrice;
};

export const getSalesPercentage = (product: ProductInterface) => {
    const { price_range: priceRange } = product;
    const {
        minimum_price: { final_price: minPrice },
        maximum_price: maximumPrice,
    } = priceRange;
    const maxPrice = maximumPrice ? maximumPrice.final_price : minPrice;
    return maxPrice.value! > minPrice.value! ? ((maxPrice.value! - minPrice.value!) / maxPrice.value!) * 100 : 0;
};

export const getDiscountPercent = (product: ProductInterface): number => {
    const { price_range: priceRange } = product;
    const {
        minimum_price: { discount: minDiscount },
        maximum_price: maximumPrice,
    } = priceRange;
    const maxDiscount = maximumPrice ? maximumPrice.discount : minDiscount;

    return Math.max(minDiscount?.percent_off ?? 0, maxDiscount?.percent_off ?? 0);
};

export const usePrice = (
    data?: {
        amount: number;
        baseAmount?: number;
        currencyCode?: string;
    } | null
) => {
    const currency = 'VND';
    const locale = 'vi';
    const currencyCode = data?.currencyCode || currency || 'VND';

    const { amount, baseAmount } = {
        ...data,
    };

    const value = useMemo(() => {
        if (typeof amount !== 'number' || !currencyCode) return '';
        const currentLocale = locale ?? 'vi';
        return baseAmount
            ? formatVariantPrice({
                  amount,
                  baseAmount,
                  currencyCode,
                  locale: currentLocale,
              })
            : formatPrice({ amount, currencyCode, locale: currentLocale });
    }, [amount, baseAmount, currencyCode, locale]);

    return typeof value === 'string' ? { price: value, basePrice: null, discount: null } : value;
};

export const formatVariantPrice = ({
    amount,
    baseAmount,
    currencyCode,
    locale,
}: {
    baseAmount: number;
    amount: number;
    currencyCode: string;
    locale: string;
}) => {
    const hasDiscount = baseAmount > amount;
    const formatDiscount = new Intl.NumberFormat(locale, { style: 'percent' });
    const discount = hasDiscount ? formatDiscount.format((baseAmount - amount) / baseAmount) : null;

    const price = formatPrice({ amount, currencyCode, locale });
    const basePrice = hasDiscount ? formatPrice({ amount: baseAmount, currencyCode, locale }) : null;

    return { price, basePrice, discount };
};

export function formatPrice({
    amount,
    currencyCode,
    locale,
}: {
    amount: number;
    currencyCode: string;
    locale?: string;
}) {
    const formatCurrency = new Intl.NumberFormat(locale ?? 'vi', {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'code',
    });

    if (currencyCode === 'VND') {
        const formattedAmount = formatCurrency.format(amount);
        return formattedAmount.replace('VND', 'đ').replaceAll(/\s/g, '');
    }

    return formatCurrency.format(amount);
}
